import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Button, IconButton,
  Divider, CircularProgress, Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';

const CODE_LENGTH = 6;
const emptyCode = () => Array(CODE_LENGTH).fill('');

// Shared code-entry modal for Customer Wallet OTP — reused, unmodified, by
// every surface that debits a customer's wallet (CheckoutPage's Store Credit
// tile, CheckoutLoyaltyPanel's Apply flow, ExchangeCheckoutModal's
// customer_pays payment picker). Presentation only — the state machine lives
// in useWalletOtpVerification; this component just renders it and reports
// digit entry back up.
const CustomerWalletOtpModal = ({
  open, onClose, title, secondsLeft, resendCooldown,
  isResending, isVerifying, onVerify, onResend,
}) => {
  const [digits, setDigits] = useState(emptyCode());
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  const handleDigitChange = (index, value) => {
    const v = value.replace(/[^0-9]/g, '').slice(0, 1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = v;
      return next;
    });
    if (error) setError('');
    if (v && index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = digits.join('');
    if (code.length < CODE_LENGTH) return;
    const result = await onVerify(code);
    if (!result?.ok) {
      setError(result?.error || 'Incorrect verification code');
      setDigits(emptyCode());
      inputRefs.current[0]?.focus();
    }
  };

  const clampedSeconds = Math.max(secondsLeft, 0);
  const minutes = Math.floor(clampedSeconds / 60);
  const seconds = clampedSeconds % 60;
  const timerLow = clampedSeconds <= 30;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', m: { xs: 2, sm: 3 } } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={800} fontSize="0.95rem" color="#1E293B">{title}</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mr: -0.5 }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        <Typography fontSize="0.72rem" color="#64748B" sx={{ mb: 1.5 }}>
          Code sent to the customer's phone on file
        </Typography>

        <Stack direction="row" spacing={{ xs: 0.5, sm: 0.75 }} justifyContent="center" sx={{ mb: 1.25 }}>
          {digits.map((d, i) => (
            <Box
              key={i}
              component="input"
              inputMode="numeric"
              maxLength={1}
              value={d}
              ref={(el) => { inputRefs.current[i] = el; }}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              sx={{
                width: { xs: 28, sm: 34 }, height: { xs: 36, sm: 40 },
                textAlign: 'center', fontSize: '1.05rem', fontWeight: 700,
                border: '1.5px solid', borderColor: error ? '#EF4444' : '#E2E8F0',
                borderRadius: '8px', color: '#1E293B', fontFamily: 'inherit',
                '&:focus': { outline: 'none', borderColor: '#4F46E5', boxShadow: '0 0 0 3px #EEF2FF' },
              }}
            />
          ))}
        </Stack>

        {error && (
          <Typography fontSize="0.68rem" fontWeight={600} color="#DC2626" sx={{ mb: 1 }}>
            ✕ {error}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography fontSize="0.68rem" fontWeight={timerLow ? 700 : 400} color={timerLow ? '#DC2626' : '#94A3B8'}>
            Code expires in {minutes}:{String(seconds).padStart(2, '0')}
          </Typography>
          <Button
            size="small"
            onClick={onResend}
            disabled={resendCooldown > 0 || isResending}
            sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#4F46E5', p: 0, minWidth: 0, '&.Mui-disabled': { color: '#CBD5E1' } }}
          >
            {isResending ? <CircularProgress size={12} /> : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </Button>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={handleVerify}
          disabled={isVerifying}
          sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '8px', bgcolor: '#4F46E5', boxShadow: 'none', '&:hover': { bgcolor: '#4338CA' } }}
        >
          {isVerifying ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Verify & unlock'}
        </Button>

        <Typography fontSize="0.65rem" color="#94A3B8" sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #F1F5F9', lineHeight: 1.5 }}>
          Customer can't receive the SMS? A store manager can look up this code in the audit dashboard and read it out.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerWalletOtpModal;
